import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { type Range, useVirtualizer } from "@tanstack/react-virtual";
import type {
  CuratedModelTag,
  CuratedSectionsOpen
} from "../data/curated-models";
import type { ModelRelease } from "../types/model";
import type {
  GroupMode,
  ReleaseFilters,
  ReleaseTagFilterInteraction,
  ReleaseTagFilterKey
} from "../lib/query-models";
import { formatCount } from "../lib/format";
import {
  buildSectionCounts,
  buildSections,
  type Section
} from "../lib/release-sections";
import { useI18n } from "../hooks/use-i18n";
import {
  chunkIntoRows,
  extractStickyVirtualIndexes,
  getGridColumnCount
} from "../lib/virtual-grid";
import { packVendorGroups, type VendorSegment } from "../lib/vendor-packing";
import {
  planVendorLabelExpansion,
  vendorLabelOverflows
} from "../lib/vendor-label-expansion";
import { hasEnteredView } from "../lib/entrance-reveal";
import { gsap, useGSAP } from "../lib/gsap";
import {
  buildResultCardMotionPlan,
  type ResultCardBounds,
  type ResultMotionSnapshot
} from "../lib/result-motion";
import { stickyHeaderPushOffset } from "../lib/sticky-header";
import { CuratedModelSection } from "./CuratedModelSection";
import { FlagshipSection } from "./FlagshipSection";
import { ModelCard } from "./ModelCard";
import type { SmoothScrollController } from "./SmoothScroll";
import { UpcomingSection } from "./UpcomingSection";
import { VendorMark } from "./VendorMark";
import type { OrganizationIndex } from "../lib/organizations";
import { organizationName } from "../lib/organizations";

type ReleaseRow =
  | {
      kind: "section";
      key: string;
      title: string;
      subtitle: string | null;
      countLabel: string | null;
    }
  | {
      kind: "vendor";
      key: string;
      segments: VendorSegment[];
    }
  | {
      kind: "models";
      key: string;
      records: ModelRelease[];
    };

/**
 * A vendor label is laid out at its natural width, so one that outgrows the
 * columns its own cards occupy is faded out at the column edge rather than
 * squeezed into its neighbour. Hovering lifts the fade and floats the whole
 * label over the vendors beside it, growing leftward once the row runs out of
 * room on the right. The dataset flag is written directly because a hover on a
 * virtualized list should not re-render it.
 */
function expandVendorLabel(event: ReactMouseEvent<HTMLSpanElement>) {
  const segment = event.currentTarget;
  const label = segment.querySelector<HTMLElement>(".vendor-block__attribution");
  const row = segment.parentElement;
  if (!label || !row) return;

  const labelRect = label.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();
  const expansion = planVendorLabelExpansion({
    labelLeft: labelRect.left,
    labelWidth: labelRect.width,
    columnRight: segment.getBoundingClientRect().right,
    rowLeft: rowRect.left,
    rowRight: rowRect.right
  });
  if (!expansion.expanded) return;

  segment.style.setProperty("--vendor-label-shift", `${expansion.shift}px`);
  segment.dataset.expanded = "true";

  // A label wider than the whole row still runs past it once shifted, where
  // the surface's edge cut a letter in half; fade its tail at the row's end.
  const visible = rowRect.right - (labelRect.left + expansion.shift);
  if (labelRect.width > visible + 0.5) {
    const fade = `linear-gradient(to right, #000 ${visible - 22}px, transparent ${visible}px)`;
    label.style.setProperty("-webkit-mask-image", fade);
    label.style.setProperty("mask-image", fade);
  }
}

function collapseVendorLabel(event: ReactMouseEvent<HTMLSpanElement>) {
  const segment = event.currentTarget;
  delete segment.dataset.expanded;
  segment.style.removeProperty("--vendor-label-shift");
  const label = segment.querySelector<HTMLElement>(".vendor-block__attribution");
  label?.style.removeProperty("-webkit-mask-image");
  label?.style.removeProperty("mask-image");
}

/**
 * Marks a vendor segment whose label runs past its own columns, which is the
 * only kind the stylesheet fades: a fade on a label that fits dimmed its last
 * letters with nothing for hover to reveal. The column width follows the
 * viewport and the label width follows the web fonts, so the segments are
 * watched for size and re-read once the fonts arrive.
 */
const vendorSegments = new Set<HTMLElement>();
let vendorSegmentObserver: ResizeObserver | null = null;

function markVendorLabelOverflow(segment: HTMLElement) {
  if (segment.dataset.expanded) return;
  const label = segment.querySelector<HTMLElement>(".vendor-block__attribution");
  if (!label) return;
  const count = segment.querySelector<HTMLElement>(".vendor-block__count");
  const contentRight = Math.max(
    label.getBoundingClientRect().right,
    count?.getBoundingClientRect().right ?? 0
  );
  segment.dataset.overflowing = String(
    vendorLabelOverflows(contentRight, segment.getBoundingClientRect().right)
  );
}

function observeVendorSegment(segment: HTMLSpanElement | null) {
  if (!segment) return;
  if (typeof ResizeObserver === "undefined") {
    markVendorLabelOverflow(segment);
    return;
  }
  vendorSegmentObserver ??= new ResizeObserver((entries) => {
    for (const entry of entries) {
      markVendorLabelOverflow(entry.target as HTMLElement);
    }
  });
  vendorSegmentObserver.observe(segment);
  vendorSegments.add(segment);
  return () => {
    vendorSegmentObserver?.unobserve(segment);
    vendorSegments.delete(segment);
  };
}

/**
 * How far below the surface's top edge a pinned date header sits, matching
 * the inset a pinned Flagship or Upcoming bar keeps above its title. The
 * header takes over from the scrolling one at the same offset, so the date
 * does not jump when it pins.
 */
const STICKY_HEADER_INSET = 4;

/**
 * The release surface's `scroll-padding-top` in `src/styles.css`. The
 * virtualizer scrolls a row into view with the same inset, so a keyboard
 * selection moving up lands clear of the pinned header at once instead of
 * passing under it before the browser's own scroll corrects it.
 */
const SURFACE_SCROLL_PADDING_TOP = 46;

function buildRows(sections: Section[], columns: number): {
  rows: ReleaseRow[];
  modelRowIndexes: Map<string, number>;
} {
  const rows: ReleaseRow[] = [];
  const modelRowIndexes = new Map<string, number>();
  const pushModelRows = (blockKey: string, recordRows: ModelRelease[][]) => {
    for (const records of recordRows) {
      const rowIndex = rows.length;
      const first = records[0]!;
      const last = records[records.length - 1]!;
      rows.push({
        kind: "models",
        key: `models:${blockKey}:${first.id}:${last.id}`,
        records
      });
      for (const record of records) modelRowIndexes.set(record.id, rowIndex);
    }
  };

  for (const section of sections) {
    if (section.title) {
      rows.push({
        kind: "section",
        key: `section:${section.key}`,
        title: section.title,
        subtitle: section.subtitle,
        countLabel: section.countLabel
      });
    }

    const vendorGroups = section.blocks.flatMap((block) =>
      block.title && block.organizationIds?.length
        ? [
            {
              key: block.key,
              title: block.title,
              organizationIds: block.organizationIds,
              records: block.records,
              count: block.count
            }
          ]
        : []
    );

    if (vendorGroups.length !== section.blocks.length) {
      for (const block of section.blocks) {
        pushModelRows(block.key, chunkIntoRows(block.records, columns));
      }
      continue;
    }

    for (const unit of packVendorGroups(vendorGroups, columns)) {
      rows.push({
        kind: "vendor",
        key: `vendor:${unit.key}`,
        segments: unit.segments
      });
      pushModelRows(unit.key, unit.rows);
    }
  }

  return { rows, modelRowIndexes };
}

interface VirtualModelRowProps {
  records: ModelRelease[];
  group: GroupMode;
  visitCutoff: number;
  keyboardIndex: number;
  flatIndex: Map<string, number>;
  onOpen: (record: ModelRelease) => void;
  direction: "forward" | "backward";
  motionKey: string;
  suppressEntrance: boolean;
  scrollElement: HTMLElement | null;
  organizations: OrganizationIndex;
  filters: ReleaseFilters;
  onToggleFilter: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
}

function VirtualModelRow({
  records,
  group,
  visitCutoff,
  keyboardIndex,
  flatIndex,
  onOpen,
  direction,
  motionKey,
  suppressEntrance,
  scrollElement,
  organizations,
  filters,
  onToggleFilter
}: VirtualModelRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  useGSAP(
    (_context, contextSafe) => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const row = rowRef.current;
      if (!row) return;
      const cards = gsap.utils.toArray<HTMLElement>(
        ".model-card-shell",
        row
      );
      if (!cards.length) return;

      if (suppressEntrance) {
        gsap.set(cards, {
          clearProps: "opacity,visibility,transform"
        });
        return;
      }

      gsap.set(cards, {
        autoAlpha: 0,
        y: direction === "backward" ? -8 : 8
      });

      // The observer fires after this callback has returned, so a tween built
      // inside it is not adopted by the row's context and would outlive the row
      // it belongs to. `contextSafe` hands ownership back, so the entrance is
      // reverted with everything else this row set.
      const runReveal = () => {
        gsap.to(cards, {
          autoAlpha: 1,
          y: 0,
          duration: 0.28,
          ease: "power2.out",
          stagger:
            direction === "backward"
              ? { each: 0.012, from: "end" }
              : 0.012,
          clearProps: "opacity,visibility,transform"
        });
      };
      const reveal = contextSafe ? contextSafe(runReveal) : runReveal;

      if (!("IntersectionObserver" in window)) {
        reveal();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!hasEnteredView(entries)) return;
          observer.disconnect();
          reveal();
        },
        {
          root: scrollElement,
          rootMargin: "56px 0px",
          threshold: 0.01
        }
      );
      observer.observe(row);

      return () => observer.disconnect();
    },
    {
      scope: rowRef,
      dependencies: [motionKey],
      revertOnUpdate: true
    }
  );

  return (
    <div
      ref={rowRef}
      className="model-grid"
      data-reveal-direction={direction}
    >
      {records.map((record) => {
        const index = flatIndex.get(record.id) ?? -1;
        const releaseTime = record.dates.start
          ? Date.parse(`${record.dates.start}T00:00:00Z`)
          : 0;
        return (
          <ModelCard
            key={record.id}
            record={record}
            organizations={organizations}
            showDate={group !== "date"}
            isNew={releaseTime > visitCutoff}
            keyboardActive={index === keyboardIndex}
            onOpen={onOpen}
            filters={filters}
            onToggleFilter={onToggleFilter}
          />
        );
      })}
    </div>
  );
}

interface ReleaseListProps {
  /** The loaded page of cards. */
  records: ModelRelease[];
  /** Every matching card; the headings' counts come from it, not the page. */
  countRecords: ModelRelease[];
  flagshipRecords: ModelRelease[];
  milestoneRecords: ModelRelease[];
  group: GroupMode;
  visitCutoff: number;
  keyboardIndex: number;
  onOpen: (record: ModelRelease) => void;
  onOpenCurated: (record: ModelRelease, section: CuratedModelTag) => void;
  animationKey: string;
  motionRequestRef: RefObject<ResultMotionSnapshot | null>;
  scrollElementRef: RefObject<HTMLElement | null>;
  scrollControllerRef: RefObject<SmoothScrollController | null>;
  hasMore: boolean;
  onLoadMore: () => void;
  organizations: OrganizationIndex;
  filters: ReleaseFilters;
  onToggleFilter: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
  curatedOpen: CuratedSectionsOpen;
  onToggleCurated: (section: keyof CuratedSectionsOpen) => void;
}

export function ReleaseList({
  records,
  countRecords,
  flagshipRecords,
  milestoneRecords,
  group,
  visitCutoff,
  keyboardIndex,
  onOpen,
  onOpenCurated,
  animationKey,
  motionRequestRef,
  scrollElementRef,
  scrollControllerRef,
  hasMore,
  onLoadMore,
  organizations,
  filters,
  onToggleFilter,
  curatedOpen,
  onToggleCurated
}: ReleaseListProps) {
  const { language } = useI18n();
  const container = useRef<HTMLDivElement>(null);
  const {
    flagship: flagshipOpen,
    milestone: milestoneOpen,
    upcoming: upcomingOpen
  } = curatedOpen;
  const [curatedLayoutVersion, setCuratedLayoutVersion] = useState(0);
  const [virtualizerReady, setVirtualizerReady] = useState(false);
  const [activeStickyIndex, setActiveStickyIndex] = useState(-1);
  const [layout, setLayout] = useState({
    columns: 1,
    scrollMargin: 0,
    scrollBorderTop: 0,
    viewportWidth: 0,
    viewportHeight: 0
  });
  const lastDirection = useRef<"forward" | "backward">("forward");
  const lastRequestedRecordCount = useRef<number | null>(null);
  const previousAnimationKey = useRef(animationKey);
  const handleCuratedMotionComplete = useCallback(() => {
    setCuratedLayoutVersion((current) => current + 1);
  }, []);
  const sectionCounts = useMemo(
    () => buildSectionCounts(countRecords, group, language),
    [countRecords, group, language]
  );
  const sections = useMemo(
    () => buildSections(records, group, language, organizations, sectionCounts),
    [group, language, organizations, records, sectionCounts]
  );
  const { rows, modelRowIndexes } = useMemo(
    () => buildRows(sections, layout.columns),
    [layout.columns, sections]
  );
  const flatIndex = useMemo(
    () => new Map(records.map((record, index) => [record.id, index])),
    [records]
  );
  const stickyIndexes = useMemo(
    () =>
      rows.flatMap((row, index) => (row.kind === "section" ? [index] : [])),
    [rows]
  );

  useLayoutEffect(() => {
    let frame = 0;
    let setupFrame = 0;
    let observer: ResizeObserver | null = null;
    const measure = () => {
      const element = container.current;
      const scrollElement = scrollElementRef.current;
      if (!element || !scrollElement) return;
      const columns = getGridColumnCount(element.clientWidth);
      const scrollMargin =
        element.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop;
      const scrollBorderTop = scrollElement.clientTop;
      const viewportWidth = scrollElement.clientWidth;
      const viewportHeight = scrollElement.clientHeight;
      setLayout((current) =>
        current.columns === columns &&
        Math.abs(current.scrollMargin - scrollMargin) < 0.5 &&
        current.scrollBorderTop === scrollBorderTop &&
        current.viewportWidth === viewportWidth &&
        current.viewportHeight === viewportHeight
          ? current
          : { columns, scrollMargin, scrollBorderTop, viewportWidth, viewportHeight }
      );
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const setupMeasurement = () => {
      const element = container.current;
      const scrollElement = scrollElementRef.current;
      if (!element || !scrollElement) {
        setupFrame = requestAnimationFrame(setupMeasurement);
        return;
      }

      measure();
      setVirtualizerReady(true);
      observer = new ResizeObserver(scheduleMeasure);
      observer.observe(element);
      observer.observe(scrollElement);
      // What stands above the list sets its offset: the results line and the
      // curated bars change height with the language's type sizes.
      for (
        let sibling = element.previousElementSibling;
        sibling;
        sibling = sibling.previousElementSibling
      ) {
        observer.observe(sibling);
      }
      window.addEventListener("resize", scheduleMeasure);
    };

    setupMeasurement();
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(setupFrame);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
    // Language changes the curated bars' height without resizing anything
    // observed, which left the virtualizer scrolling with a stale margin.
  }, [
    curatedLayoutVersion,
    flagshipOpen,
    language,
    milestoneOpen,
    scrollElementRef,
    upcomingOpen
  ]);

  const rangeExtractor = useCallback(
    (range: Range) =>
      extractStickyVirtualIndexes(range, stickyIndexes, activeStickyIndex),
    [activeStickyIndex, stickyIndexes]
  );

  // TanStack Virtual owns its mutable measurement API; React Compiler should
  // not attempt to memoize this hook result.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    enabled:
      rows.length > 0 &&
      virtualizerReady &&
      layout.viewportWidth > 0 &&
      layout.viewportHeight > 0,
    getScrollElement: () => scrollElementRef.current,
    // The rendered row heights. A row paints at its estimate until it is
    // measured a frame or two later, so a wrong one showed a vendor row 21px
    // under its date rule and 7px over its cards before the block jumped.
    estimateSize: (index) => {
      const row = rows[index];
      if (row?.kind === "section") return 29;
      if (row?.kind === "vendor") return 37;
      if (group === "date") return 125;
      return language === "zh" ? 146 : 144;
    },
    getItemKey: (index) => rows[index]?.key ?? index,
    // The margin is measured from the surface's border edge and the CSS
    // padding from inside its border, so the border joins the padding here;
    // otherwise the browser's own scroll follows each upward step by 1px.
    scrollPaddingStart: SURFACE_SCROLL_PADDING_TOP + layout.scrollBorderTop,
    overscan: 2,
    paddingEnd: hasMore ? 24 : 0,
    rangeExtractor,
    scrollMargin: layout.scrollMargin,
    initialRect: {
      width: layout.viewportWidth,
      height: layout.viewportHeight
    },
    initialOffset: () => scrollElementRef.current?.scrollTop ?? 0,
    useAnimationFrameWithResizeObserver: true
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const coordinatedMotion =
    previousAnimationKey.current !== animationKey &&
    motionRequestRef.current !== null;
  const furthestVirtualIndex = virtualRows.reduce(
    (furthest, row) => Math.max(furthest, row.index),
    -1
  );

  if (rowVirtualizer.scrollDirection) {
    lastDirection.current = rowVirtualizer.scrollDirection;
  }

  useLayoutEffect(() => {
    const scrollElement = scrollElementRef.current;
    const listElement = container.current;
    if (!scrollElement || !listElement) return;

    let frame = 0;
    const updateStickyPush = () => {
      frame = 0;
      const scrollTop = scrollElement.scrollTop;
      let nextActiveStickyIndex = -1;
      for (const index of stickyIndexes) {
        const measurement = rowVirtualizer.measurementsCache[index];
        if (
          !measurement ||
          measurement.start > scrollTop + STICKY_HEADER_INSET + 0.5
        ) {
          break;
        }
        nextActiveStickyIndex = index;
      }
      if (nextActiveStickyIndex !== activeStickyIndex) {
        setActiveStickyIndex(nextActiveStickyIndex);
      }

      const stickyElement = listElement.querySelector<HTMLElement>(
        ".virtual-release-row--sticky"
      );
      if (!stickyElement) return;

      const stickyIndex = Number(stickyElement.dataset.index);
      const stickyPosition = stickyIndexes.indexOf(stickyIndex);
      const nextStickyIndex = stickyIndexes[stickyPosition + 1];
      const nextMeasurement =
        nextStickyIndex === undefined
          ? undefined
          : rowVirtualizer.measurementsCache[nextStickyIndex];
      const pushOffset = nextMeasurement
        ? stickyHeaderPushOffset(
            nextMeasurement.start,
            scrollTop + STICKY_HEADER_INSET,
            stickyElement.offsetHeight
          )
        : 0;

      stickyElement.style.transform = `translate3d(0, ${pushOffset}px, 0)`;
    };
    const scheduleStickyPush = () => {
      if (frame) return;
      frame = requestAnimationFrame(updateStickyPush);
    };

    updateStickyPush();
    scrollElement.addEventListener("scroll", scheduleStickyPush, {
      passive: true
    });
    return () => {
      cancelAnimationFrame(frame);
      scrollElement.removeEventListener("scroll", scheduleStickyPush);
    };
  }, [activeStickyIndex, rowVirtualizer, scrollElementRef, stickyIndexes]);

  // A label measured before the web fonts arrived was measured in a fallback
  // face, so every mounted vendor segment is re-read once they have.
  useEffect(() => {
    let disposed = false;
    void document.fonts?.ready.then(() => {
      if (disposed) return;
      for (const segment of vendorSegments) markVendorLabelOverflow(segment);
    });
    return () => {
      disposed = true;
    };
  }, [language]);

  useEffect(() => {
    lastRequestedRecordCount.current = null;
  }, [animationKey]);

  useGSAP(
    () => {
      const changed = previousAnimationKey.current !== animationKey;
      previousAnimationKey.current = animationKey;
      const request = motionRequestRef.current;
      if (!changed || !request) return;
      motionRequestRef.current = null;

      const list = container.current;
      if (!list) return;
      const elements = new Map<string, HTMLElement>();
      const after = new Map<string, ResultCardBounds>();
      for (const card of list.querySelectorAll<HTMLElement>(
        ".model-card-shell[data-model-id]"
      )) {
        const id = card.dataset.modelId;
        if (!id) continue;
        const rect = card.getBoundingClientRect();
        elements.set(id, card);
        after.set(id, { left: rect.left, top: rect.top });
      }

      const cards = [...elements.values()];
      if (!cards.length) return;
      gsap.killTweensOf(cards);

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(cards, {
          clearProps: "opacity,visibility,transform,zIndex"
        });
        return;
      }

      const plan = buildResultCardMotionPlan(request.cards, after);
      const moving = plan.moves.flatMap((move) => {
        const element = elements.get(move.id);
        if (!element || (Math.abs(move.x) < 0.5 && Math.abs(move.y) < 0.5)) {
          return [];
        }
        gsap.set(element, { x: move.x, y: move.y });
        return [element];
      });
      const entering = plan.entering.flatMap((id) => {
        const element = elements.get(id);
        return element && id !== request.originModelId ? [element] : [];
      });
      const origin = request.originModelId
        ? elements.get(request.originModelId)
        : undefined;
      if (origin) gsap.set(origin, { zIndex: 9 });

      const duration =
        request.kind === "search" ? 0.16 : request.kind === "tag" ? 0.28 : 0.22;
      const timeline = gsap.timeline({ defaults: { overwrite: "auto" } });
      if (moving.length) {
        timeline.to(
          moving,
          {
            x: 0,
            y: 0,
            duration,
            ease: request.kind === "search" ? "power2.out" : "power3.out",
            clearProps: "transform"
          },
          0
        );
      }
      if (entering.length) {
        timeline.fromTo(
          entering,
          {
            autoAlpha: 0,
            y: request.kind === "search" ? 5 : 8,
            scale: request.kind === "search" ? 0.995 : 0.985
          },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: request.kind === "search" ? 0.14 : 0.19,
            ease: "power2.out",
            stagger:
              request.kind === "search"
                ? 0
                : { amount: Math.min(0.06, entering.length * 0.012) },
            clearProps: "opacity,visibility,transform"
          },
          request.kind === "search" ? 0 : 0.045
        );
      }
      if (origin) {
        timeline.set(origin, { clearProps: "zIndex" }, duration);
      }
    },
    {
      scope: container,
      dependencies: [animationKey],
      revertOnUpdate: true
    }
  );

  useEffect(() => {
    if (
      !hasMore ||
      furthestVirtualIndex < rows.length - 2 ||
      lastRequestedRecordCount.current === records.length
    ) {
      return;
    }

    lastRequestedRecordCount.current = records.length;
    onLoadMore();
  }, [
    furthestVirtualIndex,
    hasMore,
    onLoadMore,
    records.length,
    rows.length
  ]);

  const keyboardRecordId = records[keyboardIndex]?.id;
  const keyboardRowIndex = keyboardRecordId
    ? modelRowIndexes.get(keyboardRecordId)
    : undefined;
  useEffect(() => {
    if (keyboardRowIndex === undefined) return;
    rowVirtualizer.scrollToIndex(keyboardRowIndex, { align: "auto" });
  }, [keyboardRowIndex, rowVirtualizer]);

  return (
    <>
      {group === "date" ? (
        <>
          <FlagshipSection
            records={flagshipRecords}
            open={flagshipOpen}
            visitCutoff={visitCutoff}
            onToggle={() => onToggleCurated("flagship")}
            onOpen={(record) => onOpenCurated(record, "flagship")}
            onMotionComplete={handleCuratedMotionComplete}
            scrollElementRef={scrollElementRef}
            scrollControllerRef={scrollControllerRef}
            organizations={organizations}
            filters={filters}
            onToggleFilter={onToggleFilter}
          />
          <CuratedModelSection
            variant="milestone"
            records={milestoneRecords}
            open={milestoneOpen}
            visitCutoff={visitCutoff}
            onToggle={() => onToggleCurated("milestone")}
            onOpen={(record) => onOpenCurated(record, "milestone")}
            onMotionComplete={handleCuratedMotionComplete}
            scrollElementRef={scrollElementRef}
            scrollControllerRef={scrollControllerRef}
            organizations={organizations}
            filters={filters}
            onToggleFilter={onToggleFilter}
          />
          <UpcomingSection
            open={upcomingOpen}
            onToggle={() => onToggleCurated("upcoming")}
            onMotionComplete={handleCuratedMotionComplete}
            scrollElementRef={scrollElementRef}
            scrollControllerRef={scrollControllerRef}
            organizations={organizations}
          />
        </>
      ) : null}
      <div
        ref={container}
        className="release-list release-list--virtual"
        data-logical-model-count={records.length}
        data-virtualized="true"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;
          const sticky =
            row.kind === "section" &&
            activeStickyIndex === virtualRow.index;
          const style: CSSProperties = sticky
            ? ({
                position: "sticky",
                top: STICKY_HEADER_INSET,
                zIndex: 6,
                "--sticky-header-inset": `${STICKY_HEADER_INSET}px`
              } as CSSProperties)
            : {
                position: "absolute" as const,
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${
                  virtualRow.start - layout.scrollMargin
                }px)`
              };

          return (
            <div
              ref={rowVirtualizer.measureElement}
              className={`virtual-release-row virtual-release-row--${row.kind}${
                sticky ? " virtual-release-row--sticky" : ""
              }`}
              data-index={virtualRow.index}
              data-virtual-row-kind={row.kind}
              key={virtualRow.key}
              style={style}
            >
              {row.kind === "section" ? (
                <header className="release-section__header">
                  <h2>{row.title}</h2>
                  {row.subtitle ? <span>{row.subtitle}</span> : null}
                  <i aria-hidden="true" />
                  {row.countLabel ? <small>{row.countLabel}</small> : null}
                </header>
              ) : row.kind === "vendor" ? (
                <header className="vendor-block__header">
                  {row.segments.map((segment) => (
                    <span
                      ref={observeVendorSegment}
                      className="vendor-block__segment"
                      key={`${segment.key}:${language}`}
                      style={{ gridColumn: `span ${segment.span}` }}
                      onMouseEnter={expandVendorLabel}
                      onMouseLeave={collapseVendorLabel}
                    >
                      <span
                        className="vendor-block__attribution"
                        aria-label={segment.title}
                      >
                        {segment.organizationIds.map(
                          (organizationId, index) => {
                            const organization =
                              organizations.get(organizationId);
                            const name = organizationName(
                              organizationId,
                              organizations,
                              language
                            );
                            return (
                              <span
                                className="vendor-block__organization"
                                key={organizationId}
                              >
                                {index ? (
                                  <span
                                    className="vendor-block__join"
                                    aria-hidden="true"
                                  >
                                    ×
                                  </span>
                                ) : null}
                                <VendorMark
                                  company={organization?.logoCompany ?? name}
                                />
                                <strong>{name}</strong>
                              </span>
                            );
                          }
                        )}
                      </span>
                      {segment.count > 1 ? (
                        <span className="vendor-block__count">
                          {formatCount(segment.count, "model", language)}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </header>
              ) : (
                <VirtualModelRow
                  records={row.records}
                  group={group}
                  visitCutoff={visitCutoff}
                  keyboardIndex={keyboardIndex}
                  flatIndex={flatIndex}
                  onOpen={onOpen}
                  direction={lastDirection.current}
                  motionKey={`${animationKey}:${row.key}`}
                  suppressEntrance={coordinatedMotion}
                  scrollElement={scrollElementRef.current}
                  organizations={organizations}
                  filters={filters}
                  onToggleFilter={onToggleFilter}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
